import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { ProductPurchaseServiceContext } from 'src/contexts/state-machines/purchase/ProductPurchaseServiceContext';
import { useIsMobile } from 'src/reservation-page/hooks/helpers/useIsMobile';
import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSDetailHeaderButton } from 'src/reservation-page/detail-header/RSDetailHeaderButton';
import { RSDescriptionList } from 'src/reservation-page/description-list/RSDescriptionList';
import { RSDescriptionListItem } from 'src/reservation-page/description-list/RSDescriptionListItem';
import { RSAnchor } from 'src/reservation-page/RSAnchor';
import { RSButton } from 'src/reservation-page/RSButton';
import {
  GMO_PG_PURCHASE_POPUP_WINDOW_NAME,
  openGmoPgPaymentPopupWindow,
} from 'src/utils/payment';

import styles from '../../index.module.css';
import { buildSiteUrl, buildUrl } from 'src/utils/request-url';

export interface ConfirmViewProps {}

export const ConfirmView: FC<ConfirmViewProps> = () => {
  const intl = useIntl();

  const isMobile = useIsMobile();

  const [state, send] = ProductPurchaseServiceContext.useActor();
  const dataProductDetail = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.productDetail!,
  );
  const productPublicId = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.productPublicId!,
  );
  const isMulpayOrderEnabled = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.isMulpayOrderEnabled,
  );

  const onBackButtonClick = useCallback(() => {
    send({
      type: 'BACK_FROM_CONFIRMING_TO_MAIN',
    });
  }, [send]);

  const onSubmitButtonClick = useCallback(() => {
    // GMO-PG 決済の場合はリダイレクトが発生し、同じwindowで処理すると入力中の予約情報が失われてしまうため
    // ポップアップ(全画面であれば別タブ) を開きそこで処理する
    // ユーザーの操作後、ポップアップを開くまでに API 通信等の非同期処理等が挟まっていると、ポップアップブロックの対象になりえるため、
    // ユーザー操作直後のこのタイミング popup を開き、 state に保持しておく
    if (isMulpayOrderEnabled) {
      const productPurchaseStartUrl = buildSiteUrl(
        `/products/${productPublicId}/purchase/start`,
        'next',
      );
      const absoluteStartUrl = buildUrl(productPurchaseStartUrl, 'absolute');

      const popup = openGmoPgPaymentPopupWindow(
        absoluteStartUrl,
        GMO_PG_PURCHASE_POPUP_WINDOW_NAME,
      );
      if (!popup) throw new Error('Could not open a popup');

      send({
        type: 'SET_MULPAY_POPUP_WINDOW',
        payload: { popupWindow: popup ?? null },
      });
    }

    send({
      type: 'TICKET_BOOK_SUBMIT',
    });
  }, [send, productPublicId, isMulpayOrderEnabled]);

  return (
    <>
      <div className="z-5 sticky top-0">
        <RSDetailHeader
          title={intl.formatMessage({
            defaultMessage: '回数券購入',
            id: 'ry51S6',
            description:
              'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
          })}
          leftButton={
            <RSDetailHeaderButton variant="close" onClick={onBackButtonClick}>
              {intl.formatMessage({
                defaultMessage: '戻る',
                id: 'YWXFps',
                description:
                  'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
              })}
            </RSDetailHeaderButton>
          }
        />
      </div>

      <div
        className={classnames(
          'overflow-y-auto',
          isMobile
            ? styles.mobileHeightRestriction
            : styles.pcHeightRestriction,
        )}
      >
        <div className="pc:px-4 pc:pb-4 pc:pt-4 px-2.5 pb-6 pt-3">
          <div className="mb-3">
            <div className="rs-font text-24 text-gray-1000 pc:text-28 font-bold">
              {intl.formatMessage({
                defaultMessage: 'この内容で購入しますか？',
                id: 'W4P0lW',
                description:
                  'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
              })}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center">
              {dataProductDetail.image_url && (
                <div className="mr-1.5 flex-none">
                  <img
                    className="box-border block h-[56px] rounded-[6px]"
                    src={dataProductDetail.image_url}
                    alt=""
                  />
                </div>
              )}

              <div className="flex-auto">
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                  {dataProductDetail.name}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <RSDescriptionList>
              <RSDescriptionListItem
                term={intl.formatMessage({
                  defaultMessage: '合計金額',
                  id: 'gORaGA',
                  description:
                    'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                })}
                description={
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage(
                      {
                        defaultMessage: '{priceString}（税込）',
                        id: 'lPoNrL',
                        description:
                          'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                      },
                      {
                        priceString: intl.formatNumber(
                          dataProductDetail.price,
                          {
                            style: 'currency',
                            currency: 'JPY',
                          },
                        ),
                      },
                    )}
                  </div>
                }
              />
              {(() => {
                if (
                  dataProductDetail.ticket_count === null ||
                  dataProductDetail.expiration_month === null
                ) {
                  return null;
                }

                return (
                  <>
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '利用可能数',
                        id: 'WLYWXh',
                        description:
                          'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                      description={
                        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                          {intl.formatMessage(
                            {
                              defaultMessage: '{ticketCount}回',
                              id: 'k8QyWD',
                              description:
                                'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                            },
                            {
                              ticketCount: dataProductDetail.ticket_count,
                            },
                          )}
                        </div>
                      }
                    />
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '有効期限',
                        id: '5SaMCy',
                        description:
                          'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                      description={
                        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                          {intl.formatMessage(
                            {
                              defaultMessage: '{expirationMonth}ヶ月',
                              id: '35IGFK',
                              description:
                                'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                            },
                            {
                              expirationMonth:
                                dataProductDetail.expiration_month,
                            },
                          )}
                        </div>
                      }
                    />
                  </>
                );
              })()}
              <RSDescriptionListItem
                term={intl.formatMessage({
                  defaultMessage: 'お支払い方法',
                  id: 'NsLj1A',
                  description:
                    'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                })}
                description={
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage({
                      defaultMessage: 'クレジットカード決済',
                      id: 'QD5rvw',
                      description:
                        'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                    })}
                  </div>
                }
              />
            </RSDescriptionList>
          </div>

          <div className="mb-4">
            <p className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
              {intl.formatMessage(
                {
                  defaultMessage:
                    '購入完了すると、{terms}に同意したとみなします。キャンセルその他の販売条件は、{sctl}をあわせてご確認ください。',
                  id: 'IiG+J9',
                  description:
                    'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                },
                {
                  terms: (
                    <RSAnchor
                      href="https://stores.fun/legal/reserve-user-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        defaultMessage: '利用規約',
                        id: 'MqD1Qt',
                        description:
                          'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                    </RSAnchor>
                  ),
                  sctl: (
                    <RSAnchor
                      href={buildSiteUrl(`/legal_notice`)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        defaultMessage: '特定商取引法に基づく表示',
                        id: '9iaRbd',
                        description:
                          'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
                      })}
                    </RSAnchor>
                  ),
                },
              )}
            </p>
          </div>

          <div>
            <RSButton
              variant="large"
              isBlock
              onClick={onSubmitButtonClick}
              disabled={state.value['confirming'] === 'ticketBookSubmitting'}
              isLoading={state.value['confirming'] === 'ticketBookSubmitting'}
            >
              {intl.formatMessage({
                defaultMessage: '購入確定',
                id: '8G+YsP',
                description:
                  'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/ConfirmView/index.tsx',
              })}
            </RSButton>
          </div>
        </div>
      </div>
    </>
  );
};
